import { ChangeEvent, FC, useMemo, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { DatePicker } from "components/date/DatePicker"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { MultiSelect } from "components/inputs/MultiSelect"

import { reportsService } from "services/reports/reports.service"

import { getMonthYear } from "util/formatter"

import { isMicroserviceTenant } from "config/featureFlags"

type Producer = "NUX" | "DARK" | "C2" | "ORIGINALS" | ""
type Currency = "USD" | "EUR"

export const GgrMonthlyReportDownload: FC = () => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [producers, setProducers] = useState<string[]>([])
  const [currency, setCurrency] = useState<Currency>("USD")
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())

  const producerOptions: { value: Producer; label: Producer }[] = useMemo(
    () => [
      { value: "NUX", label: "NUX" },
      { value: "DARK", label: "DARK" },
      { value: "C2", label: "C2" },
      { value: "ORIGINALS", label: "ORIGINALS" }
    ],
    []
  )

  const currencyOptions = useMemo(
    () => [
      { value: "USD", name: "USD" },
      { value: "EUR", name: "EUR" }
    ],
    []
  )

  const producerParam = useMemo(() => {
    return producers.length === 0 ? "ALL" : producers.join(",")
  }, [producers])

  const handleDownloadByProvider = () => {
    const period = getMonthYear(selectedDate)
    if (!period) return
    reportsService.getGGRMonthlyByProvider(producerParam, period.month, period.year, currency)
  }

  // TEMPORARY (testing): two mirror by-provider downloads — one computed on the monolith (MNT)
  // path, one on the authoritative game-provider-nux (microservices) path — so the numbers can be
  // compared side by side. Remove these once GGR parity is confirmed.
  const handleDownloadByProviderMonolith = () => {
    const period = getMonthYear(selectedDate)
    if (!period) return
    reportsService.getGGRMonthlyByProvider(producerParam, period.month, period.year, currency, "monolith")
  }

  const handleDownloadByProviderMicroservice = () => {
    const period = getMonthYear(selectedDate)
    if (!period) return
    reportsService.getGGRMonthlyByProvider(producerParam, period.month, period.year, currency, "microservice")
  }

  const handleDownloadByGame = () => {
    const period = getMonthYear(selectedDate)
    if (!period) return
    reportsService.getGGRMonthlyByGame(producerParam, period.month, period.year, currency)
  }

  const maxDate = useMemo(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  }, [])

  return (
    <div>
      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <div className="min-w-[120px]">
          <DropdownMenu
            title={t("ggr.currency")}
            options={currencyOptions}
            value={currency}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setCurrency(e.target.value as Currency)}
            rounded
            hideEmptyOption={true}
          />
        </div>

        <div>
          <label className="label block mb-1">
            <span className="label-text text-[rgba(6,7,21,1)] dark:text-white">{t("ggr.monthYear")}</span>
          </label>

          <DatePicker
            selected={selectedDate}
            onChange={(date: Date | null) => setSelectedDate(date)}
            showMonthYearPicker
            dateFormat="MM/yyyy"
            minDate={new Date(2000, 0, 1)}
            maxDate={maxDate}
            className={clsx(
              "input input-bordered w-full rounded-lg px-4 py-2 text-base font-medium",
              "bg-white text-black border-black/10",
              "dark:bg-[#0C0C1A] dark:text-white dark:border-[rgba(132,_0,_168,_0.6)]"
            )}
            calendarClassName="custom-datepicker-calendar"
          />

          <style>
            {`
              .custom-datepicker-calendar .react-datepicker__header .react-datepicker-year-header {
                color: #8400A8 !important;
              }
            `}
          </style>
        </div>
      </div>

      <div className="flex gap-4 mb-6 flex-wrap">
        <button className="btn btn-primary" onClick={handleDownloadByProvider} disabled={!selectedDate}>
          {t("ggr.downloadByProvider")}
        </button>
        <button className="btn btn-secondary" onClick={handleDownloadByGame} disabled={!selectedDate}>
          {t("ggr.downloadByGame")}
        </button>
        <div className="min-w-[220px]">
          <MultiSelect options={producerOptions} value={producers} onChange={setProducers} />
        </div>
      </div>

      {/* TEMPORARY (testing): mirror by-provider downloads — monolith (MNT) vs microservices
          (authoritative game-provider-nux) — for GGR parity comparison. Remove once confirmed.
          Hidden on MNT-authoritative (prod) tenants; only shown for microservice tenants. */}
      {isMicroserviceTenant() && (
        <div className="flex gap-4 mb-6 flex-wrap items-center">
          <span className="text-xs uppercase tracking-wide opacity-60 text-[rgba(6,7,21,1)] dark:text-white">
            GGR parity test (by provider):
          </span>
          <button className="btn btn-outline btn-sm" onClick={handleDownloadByProviderMonolith} disabled={!selectedDate}>
            By Provider — Monolith
          </button>
          <button className="btn btn-outline btn-sm" onClick={handleDownloadByProviderMicroservice} disabled={!selectedDate}>
            By Provider — Microservices
          </button>
        </div>
      )}
    </div>
  )
}
